import React from 'react';

/**
 * SkeletonLoader Component
 * 
 * Renders shimmering placeholder components for production-grade loading states.
 * Uses a linear gradient sliding animation.
 * 
 * @param {Object} props
 * @param {'product-card' | 'statistics-card' | 'table-row'} props.variant 
 * @param {number} [props.count=1] - Number of skeleton items to render
 * @param {string} [props.className]
 */
const SkeletonLoader = ({ variant = 'product-card', count = 1, className = '' }) => {
  const elements = Array.from({ length: count }, (_, i) => i);
  // Reusable shimmer class across all variants
  const shimmerClass = "relative overflow-hidden bg-light-border-default/50 dark:bg-brand-surface before:absolute before:inset-0 before:-translate-x-full before:animate-[shimmer_1.5s_infinite] before:bg-gradient-to-r before:from-transparent before:via-white/20 dark:before:via-white/10 before:to-transparent";

  if (variant === 'product-card') {
    return (
      <>
        {elements.map((key) => (
          <div key={key} className={`bg-white dark:bg-brand-surface p-[8px] md:p-[16px] rounded-card border border-light-border-default dark:border-brand-border-default ${className}`}>
            {/* 4:5 Image on mobile, 1:1 on desktop */}
            <div className={`w-full aspect-[4/5] md:aspect-square rounded-image mb-[8px] md:mb-[16px] ${shimmerClass}`} />
            
            {/* Title - 2 lines potentially */}
            <div className={`h-[14px] md:h-[16px] w-4/5 rounded-[4px] mb-1.5 md:mb-2 ${shimmerClass}`} />
            <div className={`h-[14px] md:h-[16px] w-3/5 rounded-[4px] mb-2 md:mb-4 ${shimmerClass}`} />
            
            {/* Price */}
            <div className={`h-[14px] md:h-[18px] w-1/3 rounded-[4px] mb-[8px] md:mb-[16px] ${shimmerClass}`} />
            
            {/* Quantity Selector + Add to Cart Button */}
            <div className="flex flex-col gap-1.5 md:gap-3">
              <div className={`h-[30px] md:h-[36px] w-[90px] md:w-[110px] rounded-input ${shimmerClass}`} />
              <div className={`h-[34px] md:h-[44px] w-full rounded-button ${shimmerClass}`} />
            </div>
          </div>
        ))}
      </>
    );
  }

  if (variant === 'statistics-card') {
    return (
      <>
        {elements.map((key) => (
          <div key={key} className={`bg-white dark:bg-brand-surface p-[24px] rounded-card shadow-elevated ${className}`}>
            {/* Icon circle */}
            <div className={`w-[40px] h-[40px] rounded-full mb-3 ${shimmerClass}`} />
            
            {/* Label */}
            <div className={`h-[14px] w-3/5 rounded-[4px] mb-2 ${shimmerClass}`} />
            
            {/* Value */}
            <div className={`h-[28px] w-1/2 rounded-[4px] ${shimmerClass}`} />
          </div>
        ))}
      </>
    );
  }

  // Fallback default
  return (
    <>
      {elements.map((key) => (
        <div key={key} className={`h-[56px] w-full rounded-[4px] ${shimmerClass} ${className}`} />
      ))}
    </>
  );
};

export default SkeletonLoader;
